import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import Multiselect from "./Multiselect";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerUsuariosGeneral,
  registrarUsuarioAdm,
  actualizarUsuarioAdm,
  activacionUsuarioAdm,
} from "../../redux/usuariosSlice";
import { obtenerRoles } from "../../redux/genericosSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import useSubmit from "../../Hooks/useSubmit";
import { getAdminUserPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { useValidation } from "../../Hooks/useValidation";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaUsuarios";

function Usuarios(props) {
  const { locale } = useLocale();
  const [adminUserPage, setAdminUserPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedRoles, setTranslatedRoles] = useState({});
  const [translatedEstados, setTranslatedEstados] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const { validation } = useValidation();
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const columns = useMemo(() => {
    const userTable = adminUserPage?.user_table ?? [];
    return [
      { label: userTable[0]?.label || 'Código', accessor: 'usuarioId', sortable: true },
      { label: userTable[1]?.label || 'Nombre', accessor: 'nombreCompleto', sortable: true },
      { label: userTable[2]?.label || 'Usuario', accessor: 'usuarioNombre', sortable: false },
      {
        label: userTable[3]?.label || 'Roles',
        accessor: 'roles',
        sortable: true,
        render: (item) => (
          <td>
            <ul>
              {item.roles.map((r, i) => (
                <li key={i}>{translatedRoles[r.rolNombre] || r.rolNombre}</li>
              ))}
            </ul>
          </td>
        ),
      },
      { label: userTable[4]?.label || 'Correo', accessor: 'correoElectronico', sortable: true },
      {
        label: userTable[5]?.label || 'Estado',
        accessor: 'estadoId',
        sortable: true,
        render: (item) => {
          const estadoTexto = item.estadoId === 4 ? "Activo" : "Inactivo";
          return (
            <td style={{ color: item.color }}>
              {translatedEstados[estadoTexto] || estadoTexto}
            </td>
          );
        },
      },
      { label: userTable[6]?.label || 'Acción', accessor: 'action', sortable: false },
    ];
  }, [adminUserPage, translatedRoles, translatedEstados]);

  const multiSelectRef = useRef(null);
  const { submitAction } = useSubmit();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
  } = useForm();

  const dispatch = useDispatch();
  let busqueda = useRef();

  const { usuarios } = useSelector((state) => state.usuarios);
  const { roles } = useSelector((state) => state.genericos);

  const showModalEdit = (item) => {
    setItemSelected(item);
    reset(item);
    setTituloModal(adminUserPage?.edit || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    setItemSelected({});
    multiSelectRef.current.resetSelectedValues();

    reset({ usuarioId: 0, nombre: "", apellido: "", telefono: "", correo: "" });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarDatos = () => {
    dispatch(obtenerUsuariosGeneral());
  };

  const cargarRoles = () => {
    dispatch(obtenerRoles());
  };

  const cargarUsuariosPorCaracter = () => {
    dispatch(obtenerUsuariosGeneral(busqueda.current.value));
  };

  const activacion = async (estado) => {
    submitAction(
      activacionUsuarioAdm,
      { usuarioId: itemSelected.usuarioId, estadoId: estado },
      () => {
        cargarDatos();
        changeRemoveState(false);
      }
    );
  };

  const onSubmit = async (data) => {
    submitAction(
      data.usuarioId > 0 ? actualizarUsuarioAdm : registrarUsuarioAdm,
      data,
      () => {
        closeModalEdit();
        cargarDatos();
      }
    );
  };

  useEffect(() => {
    cargarDatos();
    cargarRoles();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminUserPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminUserPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminUserPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los campos "Roles" y "Estado" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasUsuarios = usuarios && Array.isArray(usuarios) && usuarios.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasUsuarios) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const rolesTranslations = {};
      const estadosTranslations = {};
      const translationPromises = [];

      // Recopilar todos los valores únicos que necesitan traducción
      usuarios.forEach((item) => {
        // Traducir cada rol del usuario
        if (item.roles && Array.isArray(item.roles)) {
          item.roles.forEach((rol) => {
            if (rol.rolNombre && !rolesTranslations[rol.rolNombre]) {
              rolesTranslations[rol.rolNombre] = null; // Marcador para evitar duplicados
              translationPromises.push(
                _translateValue(SCREEN_NAME, rol.rolNombre, locale).then((translated) => {
                  rolesTranslations[rol.rolNombre] = translated;
                })
              );
            }
          });
        }
        
        // Traducir los estados
        const estadoTexto = item.estadoId === 4 ? "Activo" : "Inactivo";
        if (estadoTexto && !estadosTranslations[estadoTexto]) {
          estadosTranslations[estadoTexto] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, estadoTexto, locale).then((translated) => {
              estadosTranslations[estadoTexto] = translated;
            })
          );
        }
      });
      
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedRoles(rolesTranslations);
      setTranslatedEstados(estadosTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [usuarios, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 200, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminUserPage?.subtitleO || "Agregar");
              setValue("nombre", "apellido", "telefono", "correo", "estado");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminUserPage?.title || "Usuarios"}</h2>
        </div>

        <div className="header-admin-bar-items ml-auto">
          <input
            type="search"
            className="form-control"
            placeholder={adminUserPage?.search_input || "Búsqueda"}
            ref={busqueda}
            onChange={cargarUsuariosPorCaracter}
          />
        </div>
      </div>

      <div className="table-container">
        {usuarios?.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={usuarios}
              clase="table-adm"
              renderActionItems={(item) => (
                <td key={Math.random()}>
                  <button
                    className="modBtn text-primary "
                    onClick={() => {
                      showModalEdit(item);
                    }}
                  >
                    <i className="material-icons"></i>
                  </button>
                  <button
                    className="modBtn"
                    onClick={() => {
                      showRemoveModalEdit(item);
                    }}
                  >
                    {item.estadoId === 4 ? (
                      <i className="material-icons statusRed"></i>
                    ) : (
                      <i className="material-icons"></i>
                    )}
                  </button>
                </td>
              )}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminUserPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminUserPage?.name || "Nombre completo"}</label>
            <Required />
            <input
              className="form-control"
              name="nombreCompleto"
              {...register("nombreCompleto", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.nombreCompleto && (
              <p className="error">{errors.nombreCompleto.message}</p>
            )}
          </div>

          <div className="form-group">
            <label>{adminUserPage?.user_name || "Nombre de usuario"}</label>
            <Required />
            <input
              className="form-control"
              name="usuarioNombre"
              {...register("usuarioNombre", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.usuarioNombre && (
              <p className="error">{errors.usuarioNombre.message}</p>
            )}
          </div>

          <div className="form-group">
            <label>{adminUserPage?.roles || "Roles"}</label>
            <Required />
            <Multiselect
              innerRef={multiSelectRef}
              options={roles}
              selectedValues={itemSelected.roles}
              name="roles"
              setValueArray="roles"
              displayValue="rolNombre"
              control={control}
              setValue={setValue}
              placeholder="Seleccione los roles"
              rules={{ required: validation?.required_field || "Campo requerido" }}
            />

            {errors.roles && <p className="error">{errors.roles.message}</p>}
          </div>
          <div className="form-group">
            <label>{adminUserPage?.email || "Correo"}</label>
            <input
              className="form-control"
              name="correoElectronico"
              {...register("correoElectronico", {
                required: validation?.required_field || "Campo requerido",
              })}
            />
            {errors.correoElectronico && (
              <p className="error">{errors.correcoElectronico.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminUserPage?.subtitleT || "Eliminar plantillla"}
        message={
          itemSelected.estadoId === 4
            ? adminUserPage?.question || "¿Desea desactivar el siguiente usuario?"
            : adminUserPage?.questionA || "¿Desea activar el siguiente usuario?"
        }
        item={itemSelected.usuarioNombre}
        btnGroup={
          <>
            <button
              className="button-text text-red"
              onClick={() => activacion(itemSelected.estadoId === 4 ? 5 : 4)}
            >
              {adminUserPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminUserPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default Usuarios;
